import { useMemo } from "react";
import { Box, Button, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { Link } from "@mantine/tiptap";
import { generateHTML, generateJSON } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";

const EXTENSIONS = [
  StarterKit.configure({ link: false }),
  Link,
  Highlight,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Image,
];

/**
 * Shows the compliance screen roughly as an attendee meets it, so the organizer
 * can check the wording and the links before turning the flag on. The accept
 * and decline buttons are inert here — this is a preview, not the gate.
 */
export default function CompliancePreview({
  opened,
  onClose,
  title,
  body,
  languageCode,
}) {
  // Everything is round-tripped through the editor's own schema before it is
  // rendered, including bodies that arrive as plain HTML. The policy is written
  // by a workspace admin but read here by a sysAdmin, so handing the stored
  // string straight to the DOM would let one admin run script in another's
  // session. generateJSON drops any node, mark or attribute the schema does not
  // know, which is what makes the output safe to inject.
  const html = useMemo(() => {
    if (!body) return "";

    try {
      const doc =
        typeof body === "string" && body.trim().startsWith("{")
          ? JSON.parse(body)
          : generateJSON(String(body), EXTENSIONS);

      return generateHTML(doc, EXTENSIONS);
    } catch {
      return "";
    }
  }, [body]);

  return (
    <Modal opened={opened} onClose={onClose} title="Attendee preview" centered>
      <Stack gap="md">
        <Title order={3} lang={languageCode || undefined}>
          {title || "Compliance"}
        </Title>

        <Box
          lang={languageCode || undefined}
          style={{ maxHeight: 380, overflowY: "auto" }}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        <Group grow>
          <Button variant="default" disabled>
            Decline
          </Button>
          <Button disabled>Accept</Button>
        </Group>

        <Text size="xs" c="dimmed" ta="center">
          Preview only — these buttons do nothing.
        </Text>
      </Stack>
    </Modal>
  );
}
